import { memo, useMemo } from "react";

import { getExpenseTypesAPI } from "services/api/finance/expense-types/api";
import { ExpenseAppliesTo, ExpenseType } from "services/api/finance/expense-types/types";
import {
  DEFAULT_EXPENSE_TYPE_ICON,
  EXPENSE_TYPE_DESCRIPTIONS,
  EXPENSE_TYPE_ICONS,
} from "services/api/fleet/vehicles/expenses/consts";
import { buildDropdownCacheKey, DropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";
import { CardSelect } from "ui/components/CardSelect";
import { CardSelectOption } from "ui/components/CardSelect/types";
import { EXPENSE_TYPES_CACHE_TTL } from "ui/components/Dropdown/ExpenseTypeDropdown/consts";

import { ExpenseTypeGroup, ExpenseTypeStepProps } from "./types";

/**
 * Prvi korak troška: vrsta troška kao kartice, u skupinama jedna ispod druge. Nudi iste vrste
 * i istim redom kao ExpenseTypeDropdown: nadgrupe bez podgrupa, zatim podgrupe po nadgrupi.
 * Podaci dolaze iz iste predmemorije, pa nema dodatnog zahtjeva.
 */
const ExpenseTypeStep: React.FC<ExpenseTypeStepProps> = ({ value, onChange, error }) => {
  const { data } = useDropdownCache<ExpenseType[]>(
    buildDropdownCacheKey(DropdownCacheKey.ExpenseTypes, ExpenseAppliesTo.VEHICLE),
    () => getExpenseTypesAPI(ExpenseAppliesTo.VEHICLE),
    EXPENSE_TYPES_CACHE_TTL
  );

  const groups: ExpenseTypeGroup[] = useMemo(() => {
    const categories = data ?? [];
    const byOrdinal = (a: ExpenseType, b: ExpenseType) => a.ordinal - b.ordinal || a.name.localeCompare(b.name);
    const parents = categories.filter((category) => category.parentId === null).sort(byOrdinal);
    const childrenOf = (parentId: string) =>
      categories.filter((category) => category.parentId === parentId).sort(byOrdinal);

    const toOption = (type: ExpenseType): CardSelectOption => ({
      id: type.id,
      icon: EXPENSE_TYPE_ICONS[type.code ?? ""] ?? DEFAULT_EXPENSE_TYPE_ICON,
      title: type.name,
      description: EXPENSE_TYPE_DESCRIPTIONS[type.code ?? ""],
    });

    const standalone = parents.filter((parent) => childrenOf(parent.id).length === 0);

    // Nadgrupa s podgrupama je naslov skupine; nadgrupe bez podgrupa čine svoju skupinu na vrhu.
    return [
      ...(standalone.length > 0 ? [{ key: "standalone", title: "Općenito", options: standalone.map(toOption) }] : []),
      ...parents
        .filter((parent) => childrenOf(parent.id).length > 0)
        .map((parent) => ({ key: parent.id, title: parent.name, options: childrenOf(parent.id).map(toOption) })),
    ];
  }, [data]);

  return (
    <div className="flex justify-center px-4 pt-8 pb-6">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Vrsta troška</h1>
        <p className="text-center text-md text-slate-500 mb-6">
          Odaberite o kakvom je trošku riječ. Po vrsti se troškovi zbrajaju u pregledu.
        </p>
        <div className="space-y-6">
          {groups.map((group, index) => (
            <div key={group.key}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{group.title}</h3>
              {/* Jedan stupac: kartice su spojeni popis bez razmaka. Greška se piše samo ispod zadnje skupine. */}
              <CardSelect
                options={group.options}
                value={value}
                onChange={onChange}
                columns={1}
                error={index === groups.length - 1 ? error : undefined}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(ExpenseTypeStep);
